import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { submitBlog, getBlogById, editBlogServer, uploadFileToServer } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { BookOpen, AlertCircle, Image as ImageIcon } from 'lucide-react';
import ReactQuill from 'react-quill-new';
import 'react-quill-new/dist/quill.snow.css';

const WriteBlog = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { search } = useLocation();
  const editId = new URLSearchParams(search).get('edit');

  const [title, setTitle] = useState('');
  const [slug, setSlug] = useState('');
  const [excerpt, setExcerpt] = useState('');
  const [content, setContent] = useState('');
  const [image, setImage] = useState('');
  const [imageAlt, setImageAlt] = useState('');
  const [imageTitle, setImageTitle] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [isEditMode, setIsEditMode] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  
  // Advanced SEO Meta
  const [seoTitle, setSeoTitle] = useState('');
  const [seoDesc, setSeoDesc] = useState('');
  const [seoKey, setSeoKey] = useState('');
  const [seoImg, setSeoImg] = useState('');
  
  const modules = {
    toolbar: [
      [{ 'header': [1, 2, 3, false] }],
      ['bold', 'italic', 'underline', 'blockquote'],
      [{'list': 'ordered'}, {'list': 'bullet'}],
      [{ 'align': [] }],
      ['link', 'image', 'video'],
      ['clean']
    ],
  };

  useEffect(() => {
    if (editId) {
      const loadBlog = async () => {
        const blog = await getBlogById(editId);
        if (blog) {
          setTitle(blog.title || '');
          setSlug(blog.slug && decodeURIComponent(blog.slug) !== blog.id.toString() && decodeURIComponent(blog.slug) !== blog.title ? (blog.slug.includes('%') ? decodeURIComponent(blog.slug) : blog.slug) : (blog.slug || ''));
          setExcerpt(blog.excerpt || '');
          setContent(blog.content || '');
          if (blog.image && !blog.image.includes('placeholder')) setImage(blog.image);
          if (blog.image_alt) setImageAlt(blog.image_alt);
          if (blog.image_title) setImageTitle(blog.image_title);
          if (blog.seo_title) setSeoTitle(blog.seo_title);
          if (blog.seo_description) setSeoDesc(blog.seo_description);
          if (blog.seo_keywords) setSeoKey(blog.seo_keywords);
          if (blog.seo_image) setSeoImg(blog.seo_image);
          setIsEditMode(true);
        }
      };
      loadBlog();
    }
  }, [editId]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!title || !content || content === '<p><br></p>') {
      setError('Title and content are required.');
      return;
    }

    setIsSubmitting(true);
    const blogData = {
      title,
      slug,
      excerpt,
      content,
      image: image || 'https://via.placeholder.com/800x400?text=Blog+Image',
      image_alt: imageAlt,
      image_title: imageTitle,
      author: user?.name || 'Guest User',
      user_id: user?.id,
      role: user?.role || 'user',
      seo_title: seoTitle,
      seo_description: seoDesc,
      seo_keywords: seoKey,
      seo_image: seoImg
    };

    let res;
    if (isEditMode) {
      blogData.id = editId;
      res = await editBlogServer(blogData);
    } else {
      res = await submitBlog(blogData);
    }
    
    setIsSubmitting(false);

    if (res.success) {
      if (user?.role === 'admin') {
        alert(isEditMode ? 'Blog updated successfully!' : 'Blog published successfully!');
        navigate('/blog');
      } else {
        alert(isEditMode ? 'Your edits have been submitted for Admin approval.' : 'Your blog has been submitted and is waiting for Admin approval.');
        navigate('/admin/blogs');
      }
    } else {
      setError("Operation failed. Ensure database has all required fields.");
    }
  };

  return (
    <div className="min-h-screen bg-stone-50 py-12">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        
        <div className="mb-10 flex items-center space-x-4 border-b border-stone-200 pb-6">
          <div className="bg-saffron-100 p-3 rounded-xl border border-saffron-200">
            <BookOpen className="h-6 w-6 text-saffron-600" />
          </div>
          <div>
            <h1 className="text-3xl font-bold text-stone-900">{isEditMode ? 'Edit Blog Post' : 'Write a Blog Post'}</h1>
            <p className="text-stone-600">Share your spiritual journey, historic knowledge, or local updates.</p>
          </div>
        </div>

        {error && (
          <div className="mb-6 p-4 bg-red-50 text-red-700 border border-red-200 rounded-xl flex items-center">
            <AlertCircle className="w-5 h-5 mr-2" /> {error}
          </div>
        )}

        <div className="bg-white rounded-2xl shadow-sm border border-stone-200 p-6 md:p-8">
          <form onSubmit={handleSubmit} className="space-y-6">
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-semibold text-stone-700 mb-1">Title</label>
                <input 
                  type="text" 
                  maxLength={100}
                  required
                  value={title}
                  onChange={e => setTitle(e.target.value)}
                  placeholder="Enter an engaging title..."
                  className="w-full border border-stone-300 rounded-xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-saffron-500 font-medium text-lg placeholder-stone-400"
                />
              </div>
              <div>
                <label className="block text-sm font-semibold text-stone-700 mb-1">URL Slug <span className="text-xs font-normal text-stone-400">(Optional)</span></label>
                <input 
                  type="text" 
                  maxLength={100}
                  value={slug}
                  onChange={e => setSlug(e.target.value.toLowerCase().replace(/[\s_]+/g, '-').replace(/[^\w-]/g, ''))}
                  placeholder="e.g. spiritual-journey"
                  className="w-full border border-stone-300 rounded-xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-saffron-500 font-medium text-lg placeholder-stone-400 transition"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-semibold text-stone-700 mb-1">Short Excerpt</label>
              <textarea 
                maxLength={200}
                rows={2}
                value={excerpt}
                onChange={e => setExcerpt(e.target.value)}
                placeholder="A brief summary of your post to show on the public feed."
                className="w-full border border-stone-300 rounded-xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-saffron-500 text-stone-700 placeholder-stone-400 resize-none"
              />
            </div>

            <div>
              <label className="block text-sm font-semibold text-stone-700 mb-1 flex items-center">
                <ImageIcon className="w-4 h-4 mr-1 text-stone-400" /> Cover Image
              </label>
              <div className="flex flex-col sm:flex-row sm:items-center gap-4">
                <input 
                  type="url"
                  value={image.startsWith('data:') ? '' : image}
                  onChange={e => setImage(e.target.value)}
                  placeholder="Paste URL..."
                  className="w-full flex-1 border border-stone-300 rounded-xl px-4 py-2 focus:outline-none focus:ring-2 focus:ring-saffron-500 text-stone-700 bg-stone-50"
                />
                <span className="text-stone-400 font-medium hidden sm:inline">OR</span>
                <input 
                  type="file" 
                  accept="image/*"
                  onChange={async (e) => {
                    const file = e.target.files[0];
                    if (file) {
                      setIsUploading(true);
                      const res = await uploadFileToServer(file);
                      if (res.success) {
                        setImage(res.url);
                      } else {
                        alert("Failed to upload image: " + res.message);
                      }
                      setIsUploading(false);
                    }
                  }}
                  disabled={isUploading}
                  className="w-full sm:w-auto max-w-full text-sm text-stone-500 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-saffron-50 file:text-saffron-700 hover:file:bg-saffron-100 cursor-pointer disabled:opacity-50"
                />
              </div>
              <div className="flex flex-col sm:flex-row gap-4 mt-3">
                <div className="flex-1">
                  <input type="text" value={imageAlt} onChange={e=>setImageAlt(e.target.value)} placeholder="Alt Text (e.g. Beautiful temple at sunrise)" className="w-full border border-stone-300 rounded-xl px-4 py-2 focus:outline-none focus:ring-2 focus:ring-saffron-500 text-sm bg-stone-50 placeholder-stone-400" />
                </div>
                <div className="flex-1">
                  <input type="text" value={imageTitle} onChange={e=>setImageTitle(e.target.value)} placeholder="Title Text (optional hover tooltip)" className="w-full border border-stone-300 rounded-xl px-4 py-2 focus:outline-none focus:ring-2 focus:ring-saffron-500 text-sm bg-stone-50 placeholder-stone-400" />
                </div>
              </div>
              {image && (
                <div className="mt-3 relative h-32 w-48 rounded-lg overflow-hidden border border-stone-300 shadow-sm">
                  <img src={image} alt={imageAlt || "Cover Preview"} title={imageTitle} className="w-full h-full object-cover" />
                </div>
              )}
            </div>

            <div className="mb-8 h-[30rem]">
              <label className="block text-sm font-semibold text-stone-700 mb-1">Article Content</label>
              <ReactQuill 
                theme="snow" 
                value={content} 
                onChange={setContent} 
                modules={modules}
                placeholder="Start pouring your thoughts out... Link text, add images from URLs, structure your layout!"
                className="bg-white rounded-b-lg border-stone-300 pb-12 h-96" 
              />
            </div>

            {/* SEO SECTION */}
            <div className="mt-8 pt-12 border-t border-stone-200">
              <h3 className="text-lg font-bold text-stone-800 mb-4 flex items-center">
                Search Engine Optimization 
                <span className="ml-2 text-xs font-normal px-2 py-0.5 bg-saffron-100 text-saffron-700 rounded-full">Optional</span>
              </h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                <div>
                  <label className="block text-sm font-semibold text-stone-700 mb-1">SEO Title</label>
                  <input value={seoTitle} onChange={e=>setSeoTitle(e.target.value)} className="w-full border border-stone-300 rounded-lg p-2.5 outline-none focus:border-saffron-500 transition" placeholder="e.g. My Awesome Spirit Journey..." />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-stone-700 mb-1">SEO Keywords</label>
                  <input value={seoKey} onChange={e=>setSeoKey(e.target.value)} className="w-full border border-stone-300 rounded-lg p-2.5 outline-none focus:border-saffron-500 transition" placeholder="e.g. spirit, meditation, peace" />
                </div>
              </div>

              <div className="mb-4">
                <label className="block text-sm font-semibold text-stone-700 mb-1">SEO Description</label>
                <textarea rows={2} value={seoDesc} onChange={e=>setSeoDesc(e.target.value)} className="w-full border border-stone-300 rounded-lg p-2.5 outline-none focus:border-saffron-500 transition resize-none" placeholder="Brief metadata description for Google search results..."></textarea>
              </div>

              <div>
                <label className="block text-sm font-semibold text-stone-700 mb-1">SEO Social Thumbnail</label>
                <div className="flex space-x-2 items-start">
                  <div className="flex-1">
                    <input value={seoImg} onChange={e=>setSeoImg(e.target.value)} className="w-full border border-stone-300 rounded-lg p-2.5 outline-none focus:border-saffron-500 transition" placeholder="Image URL for Facebook/WhatsApp sharing" />
                    <div className="mt-2 flex border-t border-transparent pt-1 items-center justify-between">
                      <div className="text-xs text-saffron-600 truncate flex items-center">
                        {seoImg && <img src={seoImg} alt="SEO Preview" className="w-8 h-8 object-cover rounded mr-2" />}
                        {seoImg && `Preview SEO Thumbnail`}
                      </div>
                      <label className="cursor-pointer bg-stone-100 hover:bg-stone-200 text-stone-700 text-xs px-3 py-1.5 rounded border border-stone-300 transition-colors flex items-center">
                          Browse File
                         <input type="file" className="hidden" accept="image/*" onChange={async (e) => {
                            if(e.target.files[0]) {
                              const res = await uploadFileToServer(e.target.files[0]);
                              if(res.success) setSeoImg(res.url);
                            }
                         }} />
                      </label>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="pt-4 flex items-center justify-between border-t border-stone-100">
              <p className="text-sm text-stone-500">
                {user?.role === 'admin' 
                  ? "As an Admin, your post will be published immediately." 
                  : "Your post will require approval before going live."}
              </p>
              <button
                type="submit"
                disabled={isSubmitting || isUploading}
                className="px-8 py-3 bg-saffron-600 text-white font-bold rounded-xl hover:bg-saffron-700 transition disabled:bg-stone-400"
              >
                {isUploading ? 'Uploading Image...' : (isSubmitting ? (isEditMode ? 'Updating...' : 'Submitting...') : (isEditMode ? 'Update Post' : 'Submit Post'))}
              </button>
            </div>

          </form>
        </div>

      </div>
    </div>
  );
};

export default WriteBlog;
